import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ModalPlant } from '../modalPlant';
import { ModalAddUpdateOrderService } from '../../requestServices/components/ScreenServices/components/TableOrderOfService/components/ModalAddEditOrderService';
import { playAlertSound } from '../../../utils/alertSound';
import { renderBoldTags, stripHtmlTags } from '../../../utils/renderBoldTags';

export type AlertToastType = 'ALARME' | 'OFFLINE' | 'VERIFICATION_CODE';

export interface AlertToastData {
  type?: AlertToastType | string;
  title?: string;
  description?: string;
  idPlant?: string | null;
  plantName?: string | null;
  createdAt?: string;
  playSound?: boolean;
  // Código de verificação da concessionária (type VERIFICATION_CODE)
  code?: string;
  brand?: string | null;
  idUnitConsumer?: string | null;
  unitConsumerName?: string | null;
  // Alertas com a mesma chave não são empilhados de novo
  dedupeKey?: string;
  // Removido da tela automaticamente após esse instante (ISO)
  expiresAt?: string;
  // Chamado quando o usuário fecha o toast (individualmente ou "Limpar todos")
  onDismiss?: () => void;
}

interface AlertToastItem extends AlertToastData {
  id: string;
  receivedAt: Date;
}

interface AlertToastContextType {
  alerts: AlertToastItem[];
  pushAlert: (alert: AlertToastData) => void;
  removeAlert: (id: string) => void;
  clearAlerts: () => void;
}

const AlertToastContext = createContext<AlertToastContextType>({
  alerts: [],
  pushAlert: () => {},
  removeAlert: () => {},
  clearAlerts: () => {},
});

export const useAlertToast = () => useContext(AlertToastContext);

const MAX_ALERTS = 50;

const emptyOrderServiceData = {
  description: null,
  title: null,
  address: null,
  dateInit: null,
  dateEnd: null,
  idClient: null,
  idUnitBranch: null,
  idPlant: null,
  idCollaborator: null,
};

const themeByType = (type?: string) => {
  if (type === 'VERIFICATION_CODE') {
    return {
      accent: '#7239ea',
      soft: 'rgba(114, 57, 234, 0.12)',
      icon: 'bi-envelope-check-fill',
      label: 'Código de verificação',
    };
  }

  if (type === 'OFFLINE') {
    return {
      accent: '#f1416c',
      soft: 'rgba(241, 65, 108, 0.12)',
      icon: 'bi-wifi-off',
      label: 'Usina off-line',
    };
  }

  return {
    accent: '#ffc700',
    soft: 'rgba(255, 199, 0, 0.14)',
    icon: 'bi-exclamation-triangle-fill',
    label: 'Alarme',
  };
};

const formatTime = (date: Date) =>
  date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

export const AlertToastProvider = ({ children }) => {
  const [alerts, setAlerts] = useState<AlertToastItem[]>([]);
  const alertsRef = useRef<AlertToastItem[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [plantModalId, setPlantModalId] = useState<string>('');
  const [showPlantModal, setShowPlantModal] = useState(false);
  const [orderServiceData, setOrderServiceData] = useState<any>(emptyOrderServiceData);
  const [showOrderServiceModal, setShowOrderServiceModal] = useState(false);

  const updateAlerts = useCallback((next: AlertToastItem[]) => {
    alertsRef.current = next;
    setAlerts(next);
  }, []);

  const pushAlert = useCallback((alert: AlertToastData) => {
    if (alert.dedupeKey && alertsRef.current.some((item) => item.dedupeKey === alert.dedupeKey)) {
      return;
    }

    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const receivedAt = alert.createdAt ? new Date(alert.createdAt) : new Date();

    updateAlerts([{ ...alert, id, receivedAt }, ...alertsRef.current].slice(0, MAX_ALERTS));

    if (alert.playSound !== false) {
      playAlertSound(alert.type);
    }
  }, [updateAlerts]);

  const removeAlert = useCallback((id: string) => {
    const alert = alertsRef.current.find((item) => item.id === id);
    updateAlerts(alertsRef.current.filter((item) => item.id !== id));
    alert?.onDismiss?.();
  }, [updateAlerts]);

  const clearAlerts = useCallback(() => {
    const removed = alertsRef.current;
    updateAlerts([]);
    removed.forEach((alert) => alert.onDismiss?.());
  }, [updateAlerts]);

  // Remove os toasts vencidos (ex.: código de verificação fora da validade)
  useEffect(() => {
    if (!alerts.some((alert) => alert.expiresAt)) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const valid = alertsRef.current.filter(
        (alert) => !alert.expiresAt || new Date(alert.expiresAt).getTime() > now
      );
      if (valid.length !== alertsRef.current.length) updateAlerts(valid);
    }, 15000);

    return () => clearInterval(interval);
  }, [alerts, updateAlerts]);

  const copyCode = useCallback((alert: AlertToastItem) => {
    if (!alert.code || !navigator.clipboard) return;

    navigator.clipboard
      .writeText(alert.code)
      .then(() => {
        setCopiedId(alert.id);
        setTimeout(() => setCopiedId((current) => (current === alert.id ? null : current)), 2000);
      })
      .catch(() => {});
  }, []);

  const openPlantModal = useCallback((idPlant?: string | null) => {
    if (!idPlant) return;

    setPlantModalId(idPlant);
    setShowPlantModal(true);
  }, []);

  const openOrderServiceModal = useCallback((alert: AlertToastItem) => {
    setOrderServiceData({
      ...emptyOrderServiceData,
      title: alert.plantName ? `O.S. - ${alert.plantName}` : null,
      description: alert.description ? stripHtmlTags(alert.description) : null,
      idPlant: alert.idPlant ? alert.idPlant : null,
    });
    setShowOrderServiceModal(true);
  }, []);

  const value = useMemo(
    () => ({ alerts, pushAlert, removeAlert, clearAlerts }),
    [alerts, pushAlert, removeAlert, clearAlerts]
  );

  return (
    <AlertToastContext.Provider value={value}>
      {children}

      {alerts.length > 0 && (
        <div
          className='position-fixed end-0 d-flex flex-column align-items-end'
          style={{
            bottom: '6rem',
            right: '1rem',
            zIndex: 1060,
            width: '92vw',
            maxWidth: '34rem',
            gap: '0.5rem',
          }}
        >
          <div
            className='d-flex align-items-center justify-content-between w-100 rounded px-3 py-2'
            style={{
              background: 'var(--bs-body-bg, #ffffff)',
              border: '1px solid var(--bs-border-color, #e4e6ef)',
              boxShadow: '0 0.25rem 0.75rem rgba(0, 0, 0, 0.12)',
            }}
          >
            <span className='fw-bold text-gray-700 fs-7'>
              Notificações ({alerts.length})
            </span>
            <button
              type='button'
              className='btn btn-sm btn-light-danger py-1 px-3 fs-8'
              onClick={clearAlerts}
            >
              Limpar todos
            </button>
          </div>

          <div
            className='d-flex flex-column w-100'
            style={{
              gap: '0.5rem',
              maxHeight: 'calc(100vh - 12rem)',
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingRight: '0.25rem',
            }}
          >
            {alerts.map((alert) => {
              const theme = themeByType(alert.type);
              const isVerificationCode = alert.type === 'VERIFICATION_CODE';

              return (
                <div
                  key={alert.id}
                  role='alert'
                  aria-live='assertive'
                  className='rounded d-flex align-items-start p-3'
                  style={{
                    background: 'var(--bs-body-bg, #ffffff)',
                    border: '1px solid var(--bs-border-color, #e4e6ef)',
                    borderLeftWidth: '5px',
                    borderLeftStyle: 'solid',
                    borderLeftColor: theme.accent,
                    boxShadow: '0 0.25rem 0.75rem rgba(0, 0, 0, 0.12)',
                    gap: '0.75rem',
                  }}
                >
                  <div
                    className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                    style={{
                      width: '2.5rem',
                      height: '2.5rem',
                      background: theme.soft,
                      color: theme.accent,
                    }}
                  >
                    <i className={`bi ${theme.icon} fs-3`} style={{ color: theme.accent }}></i>
                  </div>

                  <div className='flex-grow-1' style={{ minWidth: 0 }}>
                    <div className='d-flex align-items-center justify-content-between'>
                      <span className='fw-bolder text-gray-800 fs-6'>
                        {alert.title ? alert.title : theme.label}
                      </span>
                      <button
                        type='button'
                        className='btn-close btn-close-sm ms-2'
                        aria-label='Fechar'
                        onClick={() => removeAlert(alert.id)}
                      ></button>
                    </div>

                    {isVerificationCode && (
                      <div
                        className='d-flex align-items-center w-100 bg-light rounded px-2 py-1 fw-bold text-gray-900 fs-7 mt-1'
                        style={{ wordBreak: 'break-word', gap: '0.35rem' }}
                      >
                        <i
                          className='bi bi-house-door-fill fs-7 flex-shrink-0'
                          title='Unidade consumidora'
                          style={{ color: theme.accent }}
                        ></i>
                        <span>{alert.unitConsumerName || 'Unidade consumidora não identificada'}</span>
                      </div>
                    )}

                    <div
                      className='text-gray-600 fs-7 mt-1'
                      style={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
                    >
                      {alert.description ? renderBoldTags(alert.description) : alert.description}
                    </div>

                    {isVerificationCode && alert.code && (
                      <div
                        className='d-flex align-items-center justify-content-between rounded px-3 py-2 mt-2'
                        style={{ background: theme.soft, gap: '0.75rem' }}
                      >
                        <span
                          className='fw-bolder fs-2'
                          style={{ color: theme.accent, letterSpacing: '0.25em', fontFamily: 'monospace' }}
                        >
                          {alert.code}
                        </span>
                        <button
                          type='button'
                          title='Copiar código'
                          aria-label='Copiar código'
                          className='btn btn-sm py-1 px-3 fs-8 fw-bold'
                          style={{ background: theme.accent, color: '#ffffff' }}
                          onClick={() => copyCode(alert)}
                        >
                          <i
                            className={`bi ${copiedId === alert.id ? 'bi-check2' : 'bi-clipboard'} fs-8 me-1`}
                            style={{ color: '#ffffff' }}
                          ></i>
                          {copiedId === alert.id ? 'Copiado' : 'Copiar'}
                        </button>
                      </div>
                    )}

                    <div className='d-flex align-items-center justify-content-between mt-2'>
                      <div className='d-flex align-items-center' style={{ gap: '0.5rem' }}>
                        <span
                          className='badge fs-9 fw-bold'
                          style={{ background: theme.soft, color: theme.accent }}
                        >
                          {theme.label}
                        </span>

                        {isVerificationCode && alert.expiresAt && (
                          <span className='text-muted fs-8'>
                            Válido até {formatTime(new Date(alert.expiresAt))}
                          </span>
                        )}

                        {!isVerificationCode && (
                          <>
                            <button
                              type='button'
                              title='Abrir usina'
                              aria-label='Abrir usina'
                              className='btn btn-sm btn-icon btn-light-primary'
                              style={{ width: '1.85rem', height: '1.85rem' }}
                              disabled={!alert.idPlant}
                              onClick={() => openPlantModal(alert.idPlant)}
                            >
                              <i className='bi bi-arrows-angle-expand fs-8'></i>
                            </button>

                            <button
                              type='button'
                              title='Criar ordem de serviço'
                              aria-label='Criar ordem de serviço'
                              className='btn btn-sm btn-icon btn-light-primary'
                              style={{ width: '1.85rem', height: '1.85rem' }}
                              onClick={() => openOrderServiceModal(alert)}
                            >
                              <i className='bi bi-wrench-adjustable fs-8'></i>
                            </button>
                          </>
                        )}
                      </div>

                      <span className='text-muted fs-8'>{formatTime(alert.receivedAt)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showPlantModal && (
        <ModalPlant
          show={showPlantModal}
          plant={plantModalId}
          handleClose={() => {
            setShowPlantModal(false);
            setPlantModalId('');
          }}
        />
      )}

      {showOrderServiceModal && (
        <ModalAddUpdateOrderService
          show={showOrderServiceModal}
          data={orderServiceData}
          handleClose={() => {
            setShowOrderServiceModal(false);
            setOrderServiceData(emptyOrderServiceData);
          }}
        />
      )}
    </AlertToastContext.Provider>
  );
};

export default AlertToastProvider;
